import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Loader2, Video, Headphones, ChefHat, FileText, Users } from 'lucide-react';
import { useUserFavoritesContext } from '../../contexts/UserFavoritesContext';
import { detectKindFromUrl } from '../../utils/urlUtils';
import { useTrackedLinkModal } from '../../hooks';
import { ContentModal, Toast } from '../ui';
import { rmContentService } from '../../services/rmContentService';
import { ThumbnailData } from '../../types/rmContent';

interface Citation {
  url: string;
  title: string;
  description?: string;
}

interface CitationsProps {
  citations: Citation[];
  annotations?: any[];
  messageContent?: string; // Full assistant message text
  sessionId?: string;
}

export const Citations: React.FC<CitationsProps> = ({ 
  citations, 
  messageContent = '',
  sessionId
}) => {
  const navigate = useNavigate();
  
  const { createFavorite, creating, refresh, getResourceByUrl, toggleFavoriteFlag } = useUserFavoritesContext();
  const { isOpen, url, title, allowCredentials, trustDomain, openLink, closeModal } = useTrackedLinkModal();
  const [togglingCitation, setTogglingCitation] = useState<string | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [thumbnailMap, setThumbnailMap] = useState<Record<string, ThumbnailData>>({});
  const fetchedUrlsRef = useRef<string>('');

  // Fetch thumbnails for all renewmyday.com citation URLs
  useEffect(() => {
    if (!citations || citations.length === 0) return;

    const rmUrls = citations
      .map(c => c.url)
      .filter(u => u.includes('renewmyday.com'));

    if (rmUrls.length === 0) return;

    const urlsKey = rmUrls.sort().join(',');
    if (fetchedUrlsRef.current === urlsKey) return;
    fetchedUrlsRef.current = urlsKey;

    rmContentService.getThumbnailsByUrls(rmUrls)
      .then(data => setThumbnailMap(data))
      .catch(() => { /* thumbnails are non-critical, render with gradient fallback */ });
  }, [citations]);

  const getKindIcon = (kind: string) => {
    switch (kind) {
      case 'video': return Video;
      case 'audio': return Headphones;
      case 'recipe': return ChefHat;
      case 'article': return FileText;
      case 'meeting': return Users;
      default: return FileText;
    }
  };

  const getKindBgClass = (kind: string) => {
    switch (kind) {
      case 'video': return 'bg-rm-primary-700/15';
      case 'audio': return 'bg-purple-100';
      case 'recipe': return 'bg-amber-100';
      default: return 'bg-rm-primary-700/10';
    }
  };

  const getKindBadgeClass = (kind: string) => {
    switch (kind) {
      case 'video': return 'bg-rm-primary-700/10 text-rm-primary-700';
      case 'audio': return 'bg-purple-100 text-purple-700';
      case 'recipe': return 'bg-amber-100 text-amber-700';
      default: return 'bg-rm-primary-700/10 text-rm-primary-700';
    }
  };

  const handleToggleFavorite = useCallback(async (citation: Citation) => {
    if (togglingCitation) return;
    setTogglingCitation(citation.url);

    try {
      const existing = getResourceByUrl(citation.url);
      if (existing) {
        await toggleFavoriteFlag(existing.id);
        const wasFavorited = existing.payload?.metadata?.isFavorited === true;
        setToastMessage(wasFavorited ? `"${citation.title}" removed from favorites` : `"${citation.title}" added to favorites`);
      } else {
        const kind = detectKindFromUrl(citation.url);
        await createFavorite({
          kind,
          payload: {
            itemId: citation.url,
            title: citation.title,
            description: citation.title,
            url: citation.url,
            metadata: {
              link: citation.url,
              details: messageContent,
              source: 'citation',
              isFavorited: true,
              citationTitle: citation.title,
              addedAt: new Date().toISOString()
            }
          }
        });
        setToastMessage(`"${citation.title}" added to favorites`);
      }
      setToastVisible(true);
      await refresh();
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    } finally {
      setTogglingCitation(null);
    }
  }, [togglingCitation, getResourceByUrl, toggleFavoriteFlag, createFavorite, messageContent, refresh]);

  // Filter out video citations that have no thumbnail
  const visibleCitations = useMemo(() => {
    return citations.filter(citation => {
      const kind = detectKindFromUrl(citation.url);
      if (kind !== 'video') return true;
      // For videos, check if we have thumbnail data
      const data = thumbnailMap[citation.url];
      if (!data) {
        // If URL is renewmyday.com and we fetched but found nothing, hide it
        return !citation.url.includes('renewmyday.com');
      }
      return !!(data.thumbnailUrl || data.image);
    });
  }, [citations, thumbnailMap]);

  if (!visibleCitations || visibleCitations.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 pt-3 border-t border-rm-neutral-600/20">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {visibleCitations.map((citation, index) => {
          const favoriteRecord = getResourceByUrl(citation.url);
          const isFavorited = favoriteRecord?.payload?.metadata?.isFavorited === true;
          const isToggling = togglingCitation === citation.url;
          const kind = detectKindFromUrl(citation.url);
          const citationNumber = (index + 1).toString();
          const KindIcon = getKindIcon(kind);
          const data = thumbnailMap[citation.url];
          const imageUrl = data?.thumbnailUrl || data?.image || null;
          const resolvedKind = data?.kind || kind;
          
          return (
            <CitationCard
              key={`${citation.url}-${index}`}
              citation={citation}
              citationNumber={citationNumber}
              kind={kind}
              KindIcon={KindIcon}
              bgClass={getKindBgClass(resolvedKind)}
              badgeClass={getKindBadgeClass(resolvedKind)}
              imageUrl={imageUrl}
              isFavorited={isFavorited}
              isToggling={isToggling}
              sessionId={sessionId}
              onOpenLink={openLink}
              onToggleFavorite={handleToggleFavorite}
              creating={creating}
            />
          );
        })}
      </div>

      {/* Content Modal */}
      <ContentModal
        url={url}
        title={title}
        isOpen={isOpen}
        onClose={closeModal}
        allowCredentials={allowCredentials}
        trustDomain={trustDomain}
      />

      {/* Toast notification for added favorites */}
      {toastVisible && (
        <Toast
          message={toastMessage}
          type="success"
          icon="heart"
          duration={4000}
          onClose={() => setToastVisible(false)}
          action={{
            label: 'View all',
            onClick: () => {
              setToastVisible(false);
              navigate('/favorites');
            }
          }}
        />
      )}
    </div>
  );
};

// Extracted card component for individual citation rendering
interface CitationCardProps {
  citation: Citation;
  citationNumber: string;
  kind: string;
  KindIcon: React.ElementType;
  bgClass: string;
  badgeClass: string;
  imageUrl: string | null;
  isFavorited: boolean;
  isToggling: boolean;
  sessionId?: string;
  onOpenLink: (url: string, title: string) => void;
  onToggleFavorite: (citation: Citation) => void;
  creating: boolean;
}

const CitationCard: React.FC<CitationCardProps> = ({
  citation,
  citationNumber,
  kind,
  KindIcon,
  bgClass,
  badgeClass,
  imageUrl,
  isFavorited,
  isToggling,
  sessionId,
  onOpenLink,
  onToggleFavorite,
  creating,
}) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const showImage = !!imageUrl && !imgError;

  return (
    <div className={`relative rounded-card overflow-hidden border border-rm-neutral-600/10 hover:border-rm-neutral-600/20 transition-all hover:shadow-soft ${bgClass}`}>
      {/* Thumbnail background image with light frosted overlay */}
      {showImage && (
        <>
          <img
            src={imageUrl}
            alt=""
            referrerPolicy="no-referrer"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${imgLoaded ? 'opacity-35' : 'opacity-0'}`}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
          />
          {imgLoaded && (
            <div className="absolute inset-0 bg-white/20" />
          )}
        </>
      )}

      {/* Content */}
      <div className="relative p-3 flex items-center gap-3">
        <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${badgeClass}`}>
          <KindIcon className="w-4 h-4" />
        </div>

        <div className="flex-1 min-w-0">
          <button
            onClick={() => onOpenLink(citation.url, citation.title)}
            className="text-left group w-full"
          >
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-xs font-semibold text-rm-primary-700/60">#{citationNumber}</span>
              <span className="text-xs text-rm-neutral-600">{kind}</span>
            </div>
            <h4 className="text-sm font-semibold text-rm-ink-900 group-hover:text-rm-primary-700 transition-colors line-clamp-2">
              {citation.title}
            </h4>
          </button>
        </div>

        {sessionId && (
          <button
            onClick={() => onToggleFavorite(citation)}
            disabled={isToggling || creating}
            className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
              isFavorited
                ? 'text-red-500 hover:text-red-400'
                : 'text-rm-neutral-600/40 hover:text-red-500 hover:bg-rm-neutral-600/10'
            } ${isToggling ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={isFavorited ? 'Remove from My Resources' : 'Add to My Resources'}
          >
            {isToggling ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
            )}
          </button>
        )}
      </div>
    </div>
  );
};
